<template>
  <div
    class="affppw"
    :class="{ 'affppw--narrow': step === 1 }"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="titleId"
  >
    <div class="affppw-header">
      <div class="affppw-featured-icon" aria-hidden="true">
        <img
          class="affppw-featured-icon__img"
          src="./assets/currency-banknote.svg"
          alt=""
          width="22"
          height="18"
        />
      </div>
      <button type="button" class="affppw-close" aria-label="Close" @click="$emit('close')">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M18 6 6 18M6 6l12 12"
            stroke="#667085"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>

    <div class="affppw-main">
      <div class="affppw-title-row">
        <h2 :id="titleId" class="affppw-title">{{ wizardTitle }}</h2>
        <span v-if="step > 1" class="affppw-effective">Effective on {{ formattedEffectiveDate }}</span>
        <span class="affppw-step-label">Step {{ step }} of {{ totalSteps }}</span>
      </div>

      <!-- ── Step 1: Effective date ── -->
      <div v-if="step === 1" class="affppw-body affppw-body--step1">
        <div class="affppw-date-row">
          <label class="affppw-inline-label" :for="dateBtnId">Effective Date*</label>
          <button :id="dateBtnId" type="button" class="affppw-date-btn" @click="openDatePicker">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M15.833 3.333H4.167c-.92 0-1.667.747-1.667 1.667v11.667c0 .92.746 1.666 1.667 1.666h11.666c.92 0 1.667-.746 1.667-1.666V5c0-.92-.746-1.667-1.667-1.667ZM13.333 1.667V5M6.667 1.667V5M2.5 8.333h15"
                stroke="#344054"
                stroke-width="1.67"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>{{ formattedEffectiveDate }}</span>
          </button>
          <DatePicker
            v-if="isDatePickerOpen"
            :isDatePickerOpen="isDatePickerOpen"
            :value="effectiveDate"
            :modal="true"
            @onApplyDate="handleApplyDate"
            @onClose="closeDatePicker"
          />
        </div>

        <div class="affppw-alert affppw-alert--flush">
          <div class="affppw-alert__icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="7.5" stroke="#667085" stroke-width="1.5" />
              <path
                d="M10 9v4M10 6.5h.01"
                stroke="#667085"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <div class="affppw-alert__content">
            <p class="affppw-alert__title">{{ step1Alert.title }}</p>
            <p class="affppw-alert__body">{{ step1Alert.body }}</p>
          </div>
        </div>
      </div>

      <!-- ── Step 2: Position / range / calculator ── -->
      <div v-else-if="step === 2" class="affppw-body">
      <div class="affppw-grid">
        <div
          class="affppw-field affppw-field--picker"
          :class="{ 'affppw-select--locked': countryCurrencyLocked }"
        >
          <CountryPicker
            class="affppw-picker"
            :class="{ 'affppw-select--locked': countryCurrencyLocked }"
            pickerLabel="Country"
            label="Type to search"
            searchLabel="Search country"
            :listSource="countryListSource"
            :useStaticFallback="countryListSource === 'static'"
            :items="countryItems"
            :value="country"
            :isforceReadonly="countryCurrencyLocked"
            :isSelectedItemRemove="!countryCurrencyLocked"
            @item-country-selected="onCountrySelected"
            @clear-selection="onCountryCleared"
            @input="onCountrySelected"
          />
        </div>

        <div
          class="affppw-field affppw-field--picker"
          :class="{ 'affppw-select--locked': countryCurrencyLocked }"
        >
          <CurrencyPicker
            class="affppw-picker"
            :class="{ 'affppw-select--locked': countryCurrencyLocked }"
            pickerLabel="Used Currency"
            label="Currency"
            searchLabel="Select Currency"
            :listSource="currencyListSource"
            :items="currencyItems"
            :value="currency"
            :isforceReadonly="countryCurrencyLocked"
            :isSelectedItemRemove="!countryCurrencyLocked"
            @item-currency-selected="onCurrencySelected"
            @clear-selection="onCurrencyCleared"
            @input="onCurrencySelected"
          />
        </div>

        <div class="affppw-field">
          <label class="affppw-label" :for="salaryInputId">Base Salary proposal</label>
          <div
            class="affppw-salary"
            :class="{ 'affppw-salary--invalid': !baseSalaryValid }"
          >
            <input
              :id="salaryInputId"
              class="affppw-salary__input"
              type="text"
              inputmode="decimal"
              :value="formattedBaseSalary"
              :aria-invalid="String(!baseSalaryValid)"
              @input="onSalaryInput"
            />
            <span class="affppw-salary__suffix">{{ salarySuffix }}</span>
          </div>
        </div>

        <div
          class="affppw-field affppw-field--picker affppw-field--pay-basis"
          :class="{ 'affppw-select--locked': countryCurrencyLocked }"
        >
          <ExSelectPicker
            class="affppw-picker"
            :class="{ 'affppw-select--locked': countryCurrencyLocked }"
            pickerLabel="Pay Basis"
            label="Pay Basis"
            searchLabel="Select Pay Basis"
            :items="payBasisOptions"
            :value="selectedPayBasis"
            :hasImageItem="false"
            :isSelectedItemRemove="!countryCurrencyLocked"
            :isforceReadonly="countryCurrencyLocked"
            @item-selected="onPayBasisSelected"
            @clear-selection="onPayBasisCleared"
            @input="onPayBasisSelected"
          />
        </div>
      </div>

      <div
        v-if="fairnessMessage"
        class="affppw-fairness"
        :class="fairnessClass"
        role="status"
      >
        <CheckCircleIcon
          v-if="fairnessIcon === 'check-circle'"
          class="affppw-fairness__icon"
        />
        <AlertTriangleIcon
          v-else-if="fairnessIcon === 'alert-triangle'"
          class="affppw-fairness__icon"
        />
        <span class="affppw-fairness__text">{{ fairnessMessage }}</span>
      </div>

      <div class="affppw-calculator">
        <h3 class="affppw-calculator__title">Fair Pay Calculator</h3>

        <template v-if="calculatorMode === 'range'">
          <div class="affppw-calculator__panel">
            <p class="affppw-calculator__subtitle">
              Employee primary position and its pay range at effective date
            </p>
            <div class="affppw-calculator__row">
              <span class="affppw-calculator__icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="2" y="1.5" width="12" height="13" rx="1.5" stroke="#667085" stroke-width="1.2" />
                  <path d="M5 5h6M5 8h6M5 11h3.5" stroke="#667085" stroke-width="1.2" stroke-linecap="round" />
                </svg>
              </span>
              <span class="affppw-calculator__strong">{{ payRange.positionCode }}</span>
            </div>
            <p class="affppw-calculator__readiness">
              @ 0%,
              <span class="affppw-pct--50">50%</span>,
              <span class="affppw-pct--100">100%</span>,
              <span class="affppw-pct--150">150%</span>
              readiness
            </p>
            <div class="affppw-calculator__row">
              <span class="affppw-calculator__icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="4" r="2" stroke="#667085" stroke-width="1.2" />
                  <path
                    d="M3 13.5c0-2.485 2.239-4.5 5-4.5s5 2.015 5 4.5"
                    stroke="#667085"
                    stroke-width="1.2"
                    stroke-linecap="round"
                  />
                </svg>
              </span>
              <span class="affppw-calculator__strong">{{ payRange.payRangeLabel }}</span>
            </div>
            <p class="affppw-calculator__meta">
              <span v-if="country?.icon" class="affppw-calculator__flag" aria-hidden="true">
                <img :src="country.icon" alt="" width="16" height="16" />
              </span>
              {{ payRange.locationLabel }}
              · {{ payRange.workMode }}
              · {{ payRange.currencyBasis }}
              ·
              <span>{{ formatPayRangeAmount(payRange.anchor0) }}</span>
              -
              <span class="affppw-pct--50">{{ formatPayRangeAmount(payRange.anchor50) }}</span>
              -
              <span class="affppw-pct--100">{{ formatPayRangeAmount(payRange.anchor100) }}</span>
              -
              <span class="affppw-pct--150">{{ formatPayRangeAmount(payRange.anchor150) }}</span>
              · {{ payRange.versionLabel }}
            </p>
          </div>
          <div class="affppw-calculator__footer">
            <div class="affppw-calculator__footer-col">
              <span class="affppw-calculator__footer-label">Current readiness</span>
              <span class="affppw-calculator__footer-value">{{ payRange.currentReadinessPct }}%</span>
            </div>
            <div class="affppw-calculator__footer-col affppw-calculator__footer-col--fair">
              <span class="affppw-calculator__footer-label">Fair Pay at current readiness</span>
              <span class="affppw-calculator__footer-amount">{{ payRange.fairPayDisplay }}</span>
              <span class="affppw-calculator__tol">±{{ tolerancePct }}%</span>
            </div>
          </div>
        </template>

        <div v-else class="affppw-calculator__empty affppw-alert affppw-alert--warn">
          <div class="affppw-alert__icon affppw-alert__icon--warn" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="7.5" stroke="#DC6803" stroke-width="1.5" />
              <path d="M10 6.5v4M10 13h.01" stroke="#DC6803" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </div>
          <div class="affppw-alert__content">
            <p class="affppw-alert__title">{{ calculatorEmpty.title }}</p>
            <p class="affppw-alert__body">{{ calculatorEmpty.body }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Step 3: Approvers ── -->
    <div v-else-if="step === 3" class="affppw-body affppw-body--step3">
      <div class="affppw-approvers">
        <p class="affppw-approvers__label">Approvers</p>

        <div v-if="!hasApprovers" class="affppw-alert affppw-alert--preapprove">
          <div class="affppw-preapprove-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="7.5" stroke="#7f56d9" stroke-width="1.5" />
              <path
                d="M10 9v4M10 6.5h.01"
                stroke="#7f56d9"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <div class="affppw-alert__content">
            <p class="affppw-alert__title">The proposal will be pre-approved by you</p>
            <p class="affppw-alert__body affppw-alert__body--preapprove">
              Because there is no default approver, this proposal will be pre-approved by you.
            </p>
          </div>
        </div>

        <hr v-if="!hasApprovers" class="affppw-approvers__divider" />

        <div
          v-for="approver in approverRows"
          :key="approver.id"
          class="affppw-approver-row"
        >
          <div class="affppw-approver-row__left">
            <span class="affppw-approver-avatar">{{ approver.displayInitials }}</span>
            <div class="affppw-approver-row__name-col">
              <span class="affppw-approver-row__name">{{ approver.name }}</span>
              <span class="affppw-approver-row__role">{{ approver.roleLabel }}</span>
            </div>
          </div>
          <button
            type="button"
            class="affppw-remove-btn"
            @click="$emit('remove-approver', approver.id)"
          >
            Remove
          </button>
        </div>

        <button
          v-if="!isApproverPickerOpen"
          type="button"
          class="affppw-add-approver"
          @click="openApproverPicker"
        >
          + Add another approver
        </button>
        <div
          v-else
          class="affppw-field affppw-field--picker affppw-approver-picker"
        >
          <ExSelectPicker
            :key="approverPickerKey"
            class="affppw-picker"
            pickerLabel="Approver"
            label="Pick an employee"
            searchLabel="Search an employee"
            placeholder="Search an employee"
            :items="availableApproverItems"
            :value="null"
            :isEmployeePicker="true"
            :hasImageItem="false"
            :isSelectedItemRemove="false"
            :useBackendSearch="true"
            :enablePagination="true"
            :hasMoreData="approverHasMore"
            :isLoadingMore="approverLoading"
            :isInitialLoading="approverLoading && !approverItems.length"
            @item-selected="onApproverSelected"
            @input="onApproverSelected"
            @search="$emit('search-approvers', $event)"
            @load-more="$emit('load-more-approvers')"
          />
        </div>
      </div>
    </div>

    </div>

    <div class="affppw-actions">
      <button
        v-if="step > 1"
        type="button"
        class="affppw-btn affppw-btn--secondary"
        @click="$emit('back')"
      >
        Back
      </button>
      <button
        type="button"
        class="affppw-btn affppw-btn--primary"
        :class="{ 'affppw-btn--disabled': !canContinue }"
        :disabled="!canContinue"
        @click="onPrimary"
      >
        {{ primaryLabel }}
      </button>
    </div>
  </div>
</template>

<script>
export const CALCULATOR_MODE = {
  RANGE: "range",
  NO_RANGE: "no-range",
  NO_POSITION: "no-position",
};

export const PAY_BASIS = {
  HOURLY: "hourly",
  MONTHLY: "monthly",
  ANNUAL: "annual",
};

export const PAY_BASIS_OPTIONS = [
  { id: PAY_BASIS.HOURLY, name: "Hourly", code: "HOURLY" },
  { id: PAY_BASIS.MONTHLY, name: "Monthly", code: "MONTHLY" },
  { id: PAY_BASIS.ANNUAL, name: "Annually", code: "ANNUAL" },
];
</script>

<script setup>
import { computed, getCurrentInstance, ref } from "vue";
import dayjs from "dayjs";
import DatePicker from "@components/DatePicker/DatePicker.vue";
import CountryPicker from "@components/CountryPicker/CountryPicker.vue";
import CurrencyPicker from "@components/CurrencyPicker/CurrencyPicker.vue";
import ExSelectPicker from "@components/SelectPicker/ExSelectPicker.vue";
import CheckCircleIcon from "@components/icons/items/Generic/CheckCircleIcon.vue";
import AlertTriangleIcon from "@components/icons/items/Generic/AlertTriangleIcon.vue";
import { formatPayRangeAmount } from "@components/utils/formatPayRangeAmount";
import {
  computeEquivalentReadiness,
  fairnessBannerClass,
  fairnessBannerIcon,
  formatFairnessMessage,
  resolveFairnessState,
} from "@components/FirstPayPlanWizard/fairnessCheck.js";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

const props = defineProps({
  step: {
    type: Number,
    default: 1,
  },
  effectiveDate: {
    type: String,
    default: () => dayjs().format("YYYY-MM-DD"),
  },
  /** Selected country `{ id, name, icon, code }` — use with CountryPicker. */
  country: { type: Object, default: null },
  /** Selected currency `{ id, name, icon, code }` — use with CurrencyPicker. */
  currency: { type: Object, default: null },
  /** Optional static country list (flags via `icon`). Empty → CountryPicker listSource. */
  countryItems: { type: Array, default: () => [] },
  /** Optional static currency list (flags via `icon`). Empty → CurrencyPicker listSource. */
  currencyItems: { type: Array, default: () => [] },
  /**
   * CountryPicker list source: `operating` (API) or `static` (demo / countryItems).
   * Storybook should use `static` with countryItems; app should use `operating`.
   */
  countryListSource: {
    type: String,
    default: "operating",
    validator: (v) => ["static", "operating"].includes(v),
  },
  /** CurrencyPicker list source: `operating` or `catalog`. Pass currencyItems in Storybook. */
  currencyListSource: {
    type: String,
    default: "operating",
    validator: (v) => ["catalog", "operating"].includes(v),
  },
  /** Fallback when currency object has no code. */
  currencyCode: { type: String, default: "USD" },
  currencySymbol: { type: String, default: "$" },
  baseSalary: { type: [Number, String], default: null },
  payBasis: { type: String, default: "annual" },
  payBasisLabel: { type: String, default: "Annually" },
  /** range | no-range | no-position */
  calculatorMode: {
    type: String,
    default: CALCULATOR_MODE.RANGE,
    validator: (v) => Object.values(CALCULATOR_MODE).includes(v),
  },
  payRange: {
    type: Object,
    default: () => ({}),
  },
  /**
   * Readiness module subscription flag from the pay-plan context endpoint (ER-11961).
   * The FairnessCheck banner is shown only when the account has the Readiness module.
   */
  readinessModuleEnabled: { type: Boolean, default: true },
  /**
   * Approver rows `{ id, name, initials, roleLabel, isDefault }`.
   * Empty list shows the pre-approved-by-you banner.
   */
  approvers: { type: Array, default: () => [] },
  /** Employee list for the Add approver dropdown (`ExSelectPicker` / employee mode). */
  approverItems: { type: Array, default: () => [] },
  /** True while a paginated approver page is in flight (parent owns the fetch). */
  approverLoading: { type: Boolean, default: false },
  /** True while more approver pages remain (mirrors the API `next`). */
  approverHasMore: { type: Boolean, default: false },
  /** When true, primary CTA is busy (parent owns API). */
  submitting: { type: Boolean, default: false },
});

const emit = defineEmits([
  "close",
  "back",
  "next",
  "save-as-draft",
  "add-approver",
  "remove-approver",
  "search-approvers",
  "load-more-approvers",
  "update:effectiveDate",
  "update:baseSalary",
  "update:country",
  "update:currency",
  "update:payBasis",
  "update:payBasisLabel",
]);

const instance = getCurrentInstance();
const uid = instance?.uid ?? 0;
const titleId = `affppw-title-${uid}`;
const dateBtnId = `affppw-date-${uid}`;
const salaryInputId = `affppw-salary-${uid}`;

const isDatePickerOpen = ref(false);
const isApproverPickerOpen = ref(false);
const approverPickerKey = ref(0);

const payBasisOptions = PAY_BASIS_OPTIONS;

const selectedPayBasis = computed(
  () => PAY_BASIS_OPTIONS.find((o) => o.id === props.payBasis) || null
);

const totalSteps = 3;

const wizardTitle = "Create First Fair Pay Plan Proposal";

const formattedEffectiveDate = computed(() => formatFairPayDate(props.effectiveDate));

const hasPayRange = computed(() => props.calculatorMode === CALCULATOR_MODE.RANGE);

const countryCurrencyLocked = computed(() => hasPayRange.value);

const step1Alert = {
  title: "You are about to create a proposal for a first pay plan",
  body:
    "This is happening when you hire a new employee. They need to understand the rules of Fair Pay and how they will be expected to increase and maintain their readiness to increase their salary. The effective date will be used to create the pay plan after the proposal is approved and committed.",
};

const hasApprovers = computed(
  () => Array.isArray(props.approvers) && props.approvers.length > 0
);

const selectedApproverIds = computed(
  () => new Set((props.approvers || []).map((approver) => approver.id))
);

const availableApproverItems = computed(() =>
  (props.approverItems || []).filter((item) => !selectedApproverIds.value.has(item.id))
);

const approverRows = computed(() =>
  (props.approvers || []).map((approver) => ({
    ...approver,
    displayInitials: approverInitials(approver),
  }))
);

const calculatorEmpty = computed(() => {
  if (props.calculatorMode === CALCULATOR_MODE.NO_POSITION) {
    return {
      title: "The employee is not assigned to a primary position",
      body: "Fair Pay can only be calculated if the employee is assigned to a primary position and that position has a Pay Range at effective date.",
    };
  }
  return {
    title: "No pay range found at the effective date",
    body: "The employee is assigned to a primary position with no pay range at the effective date. Therefor we cannot compare to a fair base salary.",
  };
});

const baseSalaryNumber = computed(() => {
  const n = Number(props.baseSalary);
  return Number.isFinite(n) ? n : null;
});

const baseSalaryValid = computed(
  () => baseSalaryNumber.value != null && baseSalaryNumber.value > 0
);

const formattedBaseSalary = computed(() => {
  if (props.baseSalary == null || props.baseSalary === "") return "";
  const n = Number(props.baseSalary);
  if (!Number.isFinite(n)) return String(props.baseSalary);
  return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
});

const salarySuffix = computed(
  () => `${props.currency?.code || props.currencyCode}/Year`
);

const equivalentReadiness = computed(() => {
  if (!hasPayRange.value) return null;
  return computeEquivalentReadiness(
    baseSalaryNumber.value,
    props.payRange.rangeMin,
    props.payRange.rangeMax
  );
});

const tolerancePct = computed(() => {
  const value = Number(props.payRange?.tolerancePct);
  return Number.isFinite(value) ? value : 2;
});

const fairnessState = computed(() => {
  if (!hasPayRange.value || baseSalaryNumber.value == null) return null;
  return resolveFairnessState({
    proposedAmount: baseSalaryNumber.value,
    fairPayAmount: props.payRange.fairPayAmount,
    rangeMin: props.payRange.rangeMin,
    rangeMax: props.payRange.rangeMax,
    currentReadinessPct: props.payRange.currentReadinessPct,
    tolerancePct: tolerancePct.value,
  });
});

const fairnessMessage = computed(() => {
  if (!fairnessState.value || !baseSalaryValid.value || !props.readinessModuleEnabled) return "";
  return formatFairnessMessage({
    state: fairnessState.value,
    proposedAmount: baseSalaryNumber.value,
    fairPayAmount: props.payRange.fairPayAmount,
    rangeMin: props.payRange.rangeMin,
    rangeMax: props.payRange.rangeMax,
    equivalentReadiness: equivalentReadiness.value,
    currentReadinessPct: props.payRange.currentReadinessPct,
    currencySymbol: props.currencySymbol,
  });
});

const fairnessClass = computed(() =>
  String(fairnessBannerClass(fairnessState.value) || "").replace(/^fppw-/, "affppw-")
);

const fairnessIcon = computed(() => fairnessBannerIcon(fairnessState.value));

const isLastStep = computed(() => props.step >= totalSteps);

const canContinue = computed(() => {
  if (props.submitting) return false;
  if (props.step === 1) return Boolean(props.effectiveDate);
  if (props.step === 2) {
    return (
      Boolean(props.country?.id || props.country?.name) &&
      Boolean(props.currency?.id || props.currency?.code) &&
      Boolean(props.payBasis) &&
      baseSalaryValid.value
    );
  }
  if (props.step === 3) return true;
  return false;
});

const primaryLabel = computed(() => (isLastStep.value ? "Save as Draft" : "Next"));

function approverInitials(approver) {
  if (approver?.initials) return approver.initials;
  const parts = String(approver?.name || approver?.fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function mapEmployeeToApprover(item) {
  const name = item.name || item.fullName || "";
  return {
    id: item.id,
    name,
    initials: item.initials || approverInitials({ name }),
    roleLabel: item.roleLabel || "Admin-added approver",
    isDefault: false,
  };
}

function openApproverPicker() {
  isApproverPickerOpen.value = true;
}

function closeApproverPicker() {
  isApproverPickerOpen.value = false;
  approverPickerKey.value += 1;
}

function onApproverSelected(item) {
  if (!item?.id) return;
  if (selectedApproverIds.value.has(item.id)) {
    closeApproverPicker();
    return;
  }
  emit("add-approver", mapEmployeeToApprover(item));
  closeApproverPicker();
}

function normalizeNumber(raw) {
  const cleaned = String(raw || "").replace(/,/g, "").trim();
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function onSalaryInput(event) {
  emit("update:baseSalary", normalizeNumber(event.target.value));
}

function onCountrySelected(item) {
  emit("update:country", item || null);
}

function onCountryCleared() {
  emit("update:country", null);
}

function onCurrencySelected(item) {
  emit("update:currency", item || null);
}

function onCurrencyCleared() {
  emit("update:currency", null);
}

function onPayBasisSelected(item) {
  if (!item?.id) return;
  emit("update:payBasis", item.id);
  emit("update:payBasisLabel", item.name);
}

function onPayBasisCleared() {
  emit("update:payBasis", "");
  emit("update:payBasisLabel", "");
}

function openDatePicker() {
  isDatePickerOpen.value = true;
}

function closeDatePicker() {
  isDatePickerOpen.value = false;
}

function formatLocalDateToISO(dateStr) {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function handleApplyDate(date) {
  if (date) emit("update:effectiveDate", formatLocalDateToISO(date));
  closeDatePicker();
}

function onPrimary() {
  if (!canContinue.value) return;
  if (!isLastStep.value) {
    emit("next");
    return;
  }
  emit("save-as-draft");
}
</script>

<style scoped>
.affppw {
  position: relative;
  z-index: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 691px;
  max-width: 100%;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 24px -4px rgba(16, 24, 40, 0.08), 0 8px 8px -4px rgba(16, 24, 40, 0.03);
  overflow: visible;
  font-family: Inter, sans-serif;
  color: #101828;
}

.affppw--narrow {
  width: 560px;
}

.affppw::before {
  content: "";
  position: absolute;
  z-index: 0;
  top: 0;
  left: 0;
  width: 216px;
  height: 216px;
  background-image: url("@components/assets/Background_pattern_decorative_new.svg");
  background-repeat: no-repeat;
  background-position: top left;
  background-size: 216px 216px;
  opacity: 0.5;
  pointer-events: none;
}

.affppw > * {
  position: relative;
  z-index: 1;
}

/* The steps (and their floating picker dropdowns — ExSelectPicker's
 * `.list-picker-container`, z-index:10) live inside `.affppw-main`. Keep that
 * wrapper above the footer so an open dropdown is not painted under the
 * Back / Save actions — which also stops those buttons swallowing item clicks. */
.affppw-main {
  z-index: 2;
}

.affppw-header {
  position: relative;
  padding: 24px 24px 20px;
}

.affppw-featured-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.affppw-featured-icon__img {
  display: block;
  width: 22px;
  height: 18px;
}

.affppw-close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.affppw-main {
  display: flex;
  flex-direction: column;
  gap: 17px;
  width: 100%;
}

.affppw-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 28px;
  padding: 0 24px;
}

.affppw-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 20px;
  color: #101828;
}

.affppw-effective {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.affppw-step-label {
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.affppw-body {
  display: flex;
  flex-direction: column;
  gap: 17px;
  padding: 0;
  padding-inline: 24px;
}

.affppw-body--step1 {
  gap: 14px;
  padding: 0 24px;
}

.affppw-alert--flush {
  margin-left: 0;
  margin-right: 0;
  width: 100%;
  box-sizing: border-box;
}

.affppw-date-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 24px;
  margin-block: 9px;
}

.affppw-body--step1 .affppw-date-row {
  padding: 0;
}

.affppw-inline-label,
.affppw-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.affppw-date-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-size: 14px;
  font-weight: 600;
  color: #344054;
  cursor: pointer;
}

.affppw-alert {
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.affppw-alert__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.affppw-alert__icon--warn {
  width: 20px;
  height: 20px;
  border: none;
  box-shadow: none;
}

.affppw-alert__content {
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.affppw-alert__title {
  margin: 0;
  padding-right: 32px;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.affppw-alert__body {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.affppw-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 17px;
  align-items: start;
}

.affppw-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  width: 100%;
}

.affppw-field--picker {
  gap: 0;
}

.affppw-field--picker > * {
  display: block;
  width: 100%;
}

.affppw-field--picker ::v-deep(.select-picker) {
  padding: 0 !important;
  margin: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
}

.affppw-field--picker ::v-deep(.container) {
  padding: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
}

/* Stretch the 352px picker; drop its 8px top margin so it lines up with salary. */
.affppw-field--picker ::v-deep(.custom-response-picker) {
  width: 100% !important;
  max-width: 100% !important;
  margin-top: 0 !important;
}

.affppw-field--picker ::v-deep(.list-picker-container),
.affppw-field--picker ::v-deep(.res-custom-list-picker) {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;
}

.affppw-field--picker ::v-deep(.res-v-list-item) {
  min-width: 0 !important;
}

.affppw-field--picker ::v-deep(.label-name) {
  margin: 0 0 6px !important;
  padding: 0 !important;
  color: #344054 !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 20px !important;
}

.affppw-field--picker ::v-deep(.select-picker .v-input) {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
}

.affppw-select {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #101828;
  text-align: left;
  cursor: pointer;
}

.affppw-select:disabled,
.affppw-select--locked {
  background: transparent;
  cursor: default;
}

.affppw-select--locked ::v-deep(.custom-response-picker .v-input__slot) {
  background-color: #f9fafb !important;
  overflow: hidden;
}

.affppw-select--locked ::v-deep(.custom-response-picker .v-input__slot fieldset) {
  background-color: #f9fafb !important;
}

.affppw-select--locked ::v-deep(.custom-response-picker input) {
  background-color: #f9fafb !important;
  cursor: default;
}

.affppw-select--locked ::v-deep(.v-input__append-inner),
.affppw-select--locked ::v-deep(.append-icon-hover) {
  display: none !important;
}

.affppw-select__text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.affppw-flag {
  display: inline-flex;
  width: 20px;
  height: 20px;
  overflow: hidden;
  border-radius: 2px;
  flex-shrink: 0;
}

.affppw-flag img {
  width: 20px;
  height: 20px;
  object-fit: cover;
}

.affppw-salary {
  display: flex;
  align-items: center;
  width: 100%;
  height: 50px;
  min-height: 50px;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  overflow: hidden;
}

.affppw-salary--invalid {
  background: #fee4e2;
}

.affppw-salary__input {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  color: #475467;
}

.affppw-salary__input:focus {
  outline: none;
}

.affppw-salary__suffix {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  font-size: 16px;
  line-height: 24px;
  color: #344054;
  white-space: nowrap;
}

.affppw-fairness {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 99px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.affppw-fairness__text {
  min-width: 0;
}

.affppw-fairness__icon {
  display: block;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  overflow: visible;
}

.affppw-fairness--above {
  background: #fffaeb;
  border: 1px solid #FEDF89;
  color: #B54708;
}

.affppw-fairness--below {
  background: #FEF3F2;
  border: 1px solid #fecdca;
  color: #B42318;
}

.affppw-fairness--exact {
  background: #ECFDF3;
  border: 1px solid #ABEFC6;
  color: #067647;
}

.affppw-fairness--out-of-range {
  background: #fef3f2;
  border: 1px solid #fecdca;
  color: #b42318;
}

.affppw-calculator {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
}

.affppw-calculator__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #101828;
}

.affppw-calculator__panel {
  padding: 16px;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.affppw-calculator__subtitle {
  margin: 0 0 8px;
  font-size: 12px;
  color: #667085;
}

.affppw-calculator__row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.affppw-calculator__icon {
  display: inline-flex;
  flex-shrink: 0;
  color: #667085;
}

.affppw-calculator__strong {
  font-size: 14px;
  font-weight: 700;
  color: #101828;
}

.affppw-calculator__readiness,
.affppw-calculator__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin: 0 0 6px;
  font-size: 14px;
  color: #344054;
}

.affppw-calculator__flag {
  display: inline-flex;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-right: 2px;
  overflow: hidden;
  border-radius: 50%;
}

.affppw-calculator__flag img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.affppw-pct--50 {
  color: #ff0303;
  font-weight: 700;
}

.affppw-pct--100 {
  color: #17b26a;
  font-weight: 700;
}

.affppw-pct--150 {
  color: #00a3ff;
  font-weight: 700;
}

/* Figma SimulationHintBanner 2332:9539 */
.affppw-calculator__footer {
  display: flex;
  align-items: center;
  gap: 23px;
  padding: 10px 14px;
  overflow: hidden;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  white-space: nowrap;
}

.affppw-calculator__footer-col {
  display: flex;
  flex: 1 0 0;
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.affppw-calculator__footer-col--fair {
  flex: 0 0 auto;
}

.affppw-calculator__footer-label {
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  color: #667085;
}

.affppw-calculator__footer-value {
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
  color: #344054;
}

.affppw-calculator__footer-amount {
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
  color: #101828;
}

.affppw-calculator__tol {
  font-size: 11px;
  font-weight: 400;
  line-height: normal;
  color: #98a2b3;
}

.affppw-calculator__empty {
  margin: 0;
}

.affppw-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 32px 24px 24px;
}

.affppw-btn {
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.affppw-btn--secondary {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
}

.affppw-btn--primary {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
}

.affppw-btn--disabled,
.affppw-btn:disabled {
  background: #e0e0e0;
  border-color: #e0e0e0;
  color: #ffffff;
  cursor: not-allowed;
}

.affppw-body--step3 {
  gap: 17px;
}

.affppw-approvers {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  box-sizing: border-box;
  overflow: visible;
}

.affppw-approvers__label {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
  color: #344054;
}

.affppw-alert--preapprove {
  align-items: center;
}

.affppw-preapprove-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.affppw-preapprove-icon::before,
.affppw-preapprove-icon::after {
  content: "";
  position: absolute;
  border: 2px solid #7f56d9;
  border-radius: 9999px;
  pointer-events: none;
}

.affppw-preapprove-icon::before {
  inset: -20%;
  opacity: 0.3;
}

.affppw-preapprove-icon::after {
  inset: -45%;
  opacity: 0.1;
}

.affppw-alert__body--preapprove {
  font-weight: 500;
  color: #344054;
}

.affppw-approvers__divider {
  margin: 0;
  border: none;
  height: 1px;
  width: 100%;
  background: #eaecf0;
}

.affppw-approver-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 15px 0;
  border-bottom: 1px solid #eaecf0;
}

.affppw-approver-row__left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.affppw-approver-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #f9f5ff;
  font-size: 11px;
  font-weight: 600;
  line-height: 13px;
  color: #7f56d9;
}

.affppw-approver-row__name-col {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.affppw-approver-row__name {
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
  color: #101828;
}

.affppw-approver-row__role {
  font-size: 11px;
  font-weight: 400;
  line-height: 13px;
  color: #667085;
}

.affppw-remove-btn {
  flex-shrink: 0;
  padding: 5px 10px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  color: #344054;
  cursor: pointer;
}

.affppw-add-approver {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 5px 10px;
  background: #ffffff;
  border: 1px solid #7f56d9;
  border-radius: 6px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  color: #7f56d9;
  cursor: pointer;
}

.affppw-approver-picker {
  width: 100%;
}

.affppw-approver-picker ::v-deep(.label-name) {
  display: none;
}
</style>
